import { Injectable } from '@angular/core';

import {
  AccountService,
  MembersService,
  SetEmailRequestDto,
  SetPhoneNumberRequestDto,
} from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of as observableOf } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as actions from '../actions/index';
import { auth } from '../auth/auth.selectors';
import { getSelectedId, ORGANISATIONS } from '../data-list.reducer';
import {
  RequestSetEmailAddressAction,
  RequestSetPhoneNumberAction,
  RequestVerificationAction,
  REQUEST_SET_EMAIL_ADDRESS,
  REQUEST_SET_PHONE_NUMBER,
  REQUEST_VERIFICATION,
} from '../profile/profile.actions';

@Injectable()
export class ProfileEffects {
  requestProfile = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.REQUEST_UPDATED_PROFILE),
      withLatestFrom(this.store.select(auth.getAuth)),
      switchMap(([action, auth]) =>
        this.v2Api.getMember({ id: auth.userId }).pipe(
          map((result) => new actions.ResponseSuccessUpdatedProfileAction(result)),
          catchError((err) =>
            observableOf(new actions.ResponseFailUpdatedProfileAction(err.message))
          )
        )
      )
    )
  );

  saveProfile = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestSaveProfile),
      withLatestFrom(
        this.store.select(auth.getAuth),
        this.store.select(getSelectedId(ORGANISATIONS))
      ),
      switchMap(([action, auth, organisationId]) =>
        this.v2Api
          .updateMemberProfile({
            id: auth.userId,
            updateMemberProfileDto: action.payload,
            organisationId,
          })
          .pipe(
            map((result) => actions.ResponseSuccessSaveProfile({ payload: result })),
            catchError((err) => observableOf(actions.ResponseFailSaveProfile({ error: err })))
          )
      )
    )
  );

  requestVerification = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestVerificationAction>(REQUEST_VERIFICATION),
      switchMap((action) =>
        this.accountService
          .requestVerification({
            verificationRequestDto: { channel: action.channel, to: action.to },
          })
          .pipe(
            map((result) => new actions.ResponseSuccessVerificationAction(result)),
            catchError((err) => observableOf(new actions.ResponseFailVerificationAction(err.code)))
          )
      )
    )
  );

  setPhoneNumber = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestSetPhoneNumberAction>(REQUEST_SET_PHONE_NUMBER),
      withLatestFrom(this.store.select(auth.getAuth)),
      switchMap(([action, auth]) =>
        this.v2Api
          .setPhoneNumber({
            id: auth.userId,
            setPhoneNumberRequestDto: {
              phoneNumber: action.payload.phoneNumber,
              code: action.payload.code,
            } as SetPhoneNumberRequestDto,
          })
          .pipe(
            map((result) => new actions.ResponseSuccessSetPhoneNumberAction(result)),
            catchError((err) => {
              return observableOf(new actions.ResponseFailSetPhoneNumberAction(err.code));
            })
          )
      )
    )
  );

  setEmail = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestSetEmailAddressAction>(REQUEST_SET_EMAIL_ADDRESS),
      withLatestFrom(this.store.select(auth.getAuth)),
      switchMap(([action, auth]) =>
        this.v2Api
          .setEmail({
            id: auth.userId,
            setEmailRequestDto: {
              email: action.payload.email,
              code: action.payload.code,
            } as SetEmailRequestDto,
          })
          .pipe(
            map((result) => new actions.ResponseSuccessSetEmailAddressAction(result)),
            catchError((err) => {
              return observableOf(new actions.ResponseFailSetEmailAddressAction(err.code));
            })
          )
      )
    )
  );
  setMemberMeta = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateMemberMetaAction),
      withLatestFrom(this.store.select(auth.getLoggedInUser)),
      filter(([action, user]) => !!user),
      switchMap(([action, user]) => {
        return this.v2Api.updateMetadata({ id: user.id, updateMemberMetaDto: action.payload }).pipe(
          map((meta) => actions.ResponseSuccessUpdateMemberMetaAction({ payload: meta })),
          catchError((err) =>
            observableOf(actions.ResponseFailUpdateMemberMetaAction({ payload: err }))
          )
        );
      })
    )
  );

  constructor(
    public store: Store<any>,
    public actions$: Actions<actions.CAAction>,
    public v2Api: MembersService,
    public accountService: AccountService
  ) {}
}
